import React, { memo, useCallback } from 'react';
import { AutoComplete } from 'primereact/autocomplete';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { Calendar } from 'primereact/calendar';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { FaLock, FaUnlock, FaFlag, FaInfoCircle } from 'react-icons/fa';
import { ProductAutoCompleteCell } from './ProductSelector';
import OperatorAuthDialog from './OperatorAuthDialog';
// import './invoice-dialog-theme.css';
import {
    ProductCell,
    QuantityCell,
    DurationCell,
    PriceCell,
    TotalCell,
    BatchCell,
    ExpiryCell,
    ActionCell,
} from './InvoiceItemRow';

const CreateInvoiceDialog = ({
    // visibility
    visible,
    onHide,
    fromUpload,

    // invoice state
    invoice,
    dialogName,
    isReadOnly: isReadOnlyProp,
    currentTime,
    invoiceFormRef,
    isCompletingRef,
    rowBumps,

    // item state
    selectedItem,
    setSelectedItem,
    manualBatchEnabledFlag,
    manualBatchMap,
    setManualBatchMap,
    batchOptionsMap,
    setBatchOptionsMap,
    excludedBatchesMap,

    // customer / prescription
    filteredCustomers,
    filteredPrescriptions,
    cusinputRef,

    // pricing
    priceEditable,
    discountApproval,
    discountEditable,
    overrideApproval,

    // operator auth
    showOperatorAuth,
    operatorAuthKey,
    invoiceOperator,
    isCreditNote,

    // header toolbar content (scan input + add button)
    itemsTableHeader,

    // handlers
    handleSearchCustomer,
    handlePrescriptionChange,
    handleDurationChange,
    handleItemChange,
    handleSubmit,
    handleDrop,
    handlePrint,
    printing,
    confirmCreditNote,
    openPaymentDialog,
    removeItem,
    fetchBatchAllocations,
    showExcludedDialog,
    calculatePrices,
    setPasswordPurpose,
    setPasswordDialogHeader,
    setPasswordDialogVisible,
    setInvoice,
    setPrescription,
    setPrescriptionDialogVisible,
    setCustomerDialogVisible,
    setIsCompleting,
    setDialogVisible,
    setInvoiceOperator,
    setShowOperatorAuth,
    setIsCreditNote,
    setIsReadOnly,
    setOrgSettings,
    setInventoryError,
    setInventoryInfo,
    setshowCreditNoteDialog,
    makeBlankItem,
    applyCustomerLoyaltyDiscount,
    handleSearchPrescriptions,
    pop,

    // operator auth dialog callbacks
    onOperatorVerified,
    onOperatorCancel,

    prodInputRefsInv,
    onCancelFromUpload,
    axiosInstance,
    handleApiError,
    canCreateInvoices,          
    canManageCustomers,        
    canManagePrescriptions,     
    canManageCreditNotes,     
    canViewPayments, 
    showMarkReady,
    markReadyAlreadySent,
    onMarkReady,
    portalUploadData,
    markReadyLoading,
}) => {
    const isReadOnly = isReadOnlyProp || !canCreateInvoices;
    const [showImagePanel, setShowImagePanel] = React.useState(false);

    // Reset panel when dialog closes or invoice changes
    React.useEffect(() => {
        if (!visible) setShowImagePanel(false);
    }, [visible]);

    const inputRefMap = prodInputRefsInv?.current || {};

    const bodyIndex = useCallback((_rowData, { rowIndex }) => rowIndex + 1, []);

    const bodyProduct = useCallback((rowData) => (
        <ProductCell
            rowData={rowData}
            isReadOnly={isReadOnly}
            handleItemChange={handleItemChange}
            inputRefMap={inputRefMap}
        />
    ), [isReadOnly, handleItemChange, inputRefMap]);

    const bodyQuantity = useCallback((rowData) => (
        <QuantityCell
            rowData={rowData}
            isReadOnly={isReadOnly}
            handleItemChange={handleItemChange}
            qtyBump={rowBumps[rowData.id]}
        />
    ), [isReadOnly, handleItemChange, rowBumps]);

    const bodyDuration = useCallback((rowData) => (
        <DurationCell
            rowData={rowData}
            isReadOnly={isReadOnly}
            handleItemChange={handleItemChange}
        />
    ), [isReadOnly, handleItemChange]);

    const bodyPrice = useCallback((rowData) => (
        <PriceCell
            rowData={rowData}
            isReadOnly={isReadOnly}
            handleItemChange={handleItemChange}
            priceEditable={priceEditable}
            overrideApproval={overrideApproval}
            setPasswordPurpose={setPasswordPurpose}
            setPasswordDialogHeader={setPasswordDialogHeader}
            setPasswordDialogVisible={setPasswordDialogVisible}
        />
    ), [isReadOnly, handleItemChange, priceEditable, overrideApproval, setPasswordPurpose, setPasswordDialogHeader, setPasswordDialogVisible]);

    const bodyTotal = useCallback((rowData) => (
        <TotalCell
            rowData={rowData}
            isReadOnly={isReadOnly}
            handleItemChange={handleItemChange}
        />
    ), [isReadOnly, handleItemChange]);

    const bodyBatch = useCallback((rowData) => (
        <BatchCell
            rowData={rowData}
            isReadOnly={isReadOnly}
            manualBatchEnabledFlag={manualBatchEnabledFlag}
            manualBatchMap={manualBatchMap}
            setManualBatchMap={setManualBatchMap}
            batchOptionsMap={batchOptionsMap}
            setBatchOptionsMap={setBatchOptionsMap}
            excludedBatchesMap={excludedBatchesMap}
            fetchBatchAllocations={fetchBatchAllocations}
            setInvoice={setInvoice}
            showExcludedDialog={showExcludedDialog}
        />
    ), [isReadOnly, manualBatchEnabledFlag, manualBatchMap, setManualBatchMap, batchOptionsMap, setBatchOptionsMap, excludedBatchesMap, fetchBatchAllocations, setInvoice, showExcludedDialog]);

    const bodyExpiry = useCallback((rowData) => (
        <ExpiryCell rowData={rowData} />
    ), []);

    const bodyAction = useCallback((rowData) => (
        <ActionCell rowData={rowData} removeItem={removeItem} />
    ), [removeItem]);
    // ── Toolbar buttons (identical in both modes) ─────────────────────────
    const headerButtons = (
        <div>
            <div className="flex flex-wrap align-items-center gap-2 sm:gap-2" style={{ margin: '10px' }}>
                <div className="text-xl font-bold mb-2">{dialogName}: {invoice?.invoice_number}</div>
                <div className="flex flex-wrap align-items-center gap-2 sm:gap-2" style={{ margin: '10px' }}></div>
                <Button label="Complete" icon="pi pi-dollar" type="submit" form="invoiceForm"
                    onClick={() => { isCompletingRef.current = true; setIsCompleting(true); }}
                    disabled={isReadOnly || invoice.is_credit || !canCreateInvoices}
                    className="p-mr-2" style={{ borderRadius: '6px', fontSize: '14px' }} size="medium" severity="success" />
                <Button label="Save" icon="pi pi-save" type="submit" form="invoiceForm"
                    onClick={() => { isCompletingRef.current = false; setIsCompleting(false); }}
                    disabled={isReadOnly || !canCreateInvoices}
                    className="p-mr-2" style={{ borderRadius: '6px', fontSize: '14px' }} size="medium" />
                <Button label="Drop" icon="pi pi-trash" onClick={(e) => handleDrop(e)}
                    disabled={isReadOnly || !invoice?.id || invoice.is_credit || !canCreateInvoices}
                    outlined className="p-mr-2" style={{ borderRadius: '6px', fontSize: '14px' }} size="medium" severity="danger" />
                <Button
                    label="Cancel" icon="pi pi-times" outlined
                    onClick={(e) => {
                        e.preventDefault();
                        if (fromUpload && onCancelFromUpload) {
                            onCancelFromUpload();
                        } else {
                            setDialogVisible(false);
                            setInvoiceOperator(null);
                            setShowOperatorAuth(false);
                            setIsCreditNote(false);
                            setIsReadOnly(false);
                            setOrgSettings();
                            setInventoryError('');
                            setInventoryInfo('');
                            setInvoice({
                                customer: null, invoiced_customer: '', date: new Date(),
                                prescription: null, remarks: '', total_price: 0,
                                discount: 0, tax_amount: 0, net_price: 0,
                                isdiscount_per: true, items: [makeBlankItem(null)],
                            });
                            pop();
                        }
                    }}
                    className="p-button-secondary" style={{ borderRadius: '6px', fontSize: '14px' }} size="medium"
                />
                {invoice?.status === 'Complete' && canManageCreditNotes &&(
                    <Button label="Credit Note" icon="pi pi-file" severity="warning"
                        disabled={invoice?.status !== 'Complete'} onClick={confirmCreditNote}
                        style={{ borderRadius: '6px', fontSize: '14px' }} size="medium" />
                )}
                {(invoice.status === 'Payment Pending' || invoice.status === 'Partially Paid') && canCreateInvoices && (
                    <Button label="Add Payment" icon="pi pi-dollar" severity="success"
                        style={{ borderRadius: '6px', fontSize: '14px' }} size="medium"
                        onClick={() => openPaymentDialog('record')} />
                )}
                {showMarkReady && (
                    <div
                        title={invoice.sms_notified_at
                                ? `SMS sent at ${new Date(invoice.sms_notified_at).toLocaleString()}`
                                : invoice.portal_notified_at
                                    ? `Portal notified at ${new Date(invoice.portal_notified_at).toLocaleString()}`
                                    : "Send SMS/Portal notification to customer"
                        }
                        style={{ display: "inline-flex" }}
                    >
                        <Button
                            label={markReadyAlreadySent ? "Customer Notified" : "Notify: Invoice Ready"}
                            icon={markReadyAlreadySent ? "pi pi-check-circle" : "pi pi-bell"}
                            severity={markReadyAlreadySent ? "secondary" : "success"}
                            disabled={markReadyAlreadySent || !invoice?.id || markReadyLoading}
                            loading={markReadyLoading}
                            onClick={(e) => { e.preventDefault(); onMarkReady(); }}
                            style={{ borderRadius: "6px", pointerEvents: (markReadyAlreadySent || !invoice?.id) ? "none" : "auto" }}
                            tooltip={!invoice?.id ? "Save the invoice first before notifying the customer" : undefined}
                            tooltipOptions={{ position: "top" }}
                        />
                    </div>
                )}
                {portalUploadData?.file_url && (
                    <Button
                        label={showImagePanel ? "Hide Image" : "View Prescription Image"}
                        icon={showImagePanel ? "pi pi-times" : "pi pi-image"}
                        severity="info" outlined
                        onClick={(e) => { e.preventDefault(); setShowImagePanel(p => !p); }}
                        style={{ borderRadius: '6px', fontSize: '14px' }} size="medium"
                    />
                )}
                <Button label="Print" loading={printing} icon="pi pi-print"
                    onClick={() => handlePrint()} outlined className="p-mr-2"
                    style={{ borderRadius: '6px', fontSize: '14px' }} size="medium" severity="info" />
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
                    <Checkbox inputId="credit" checked={invoice.is_credit}
                        onChange={e => setInvoice(prev => ({ ...prev, is_credit: e.checked }))}
                        disabled={isReadOnly || !canCreateInvoices} />
                    <label htmlFor="credit" className="font-bold">Sell on Credit</label>
                </div>
            </div>
            <div style={{ marginTop: '10px', height: '1px', background: 'linear-gradient(to right,rgb(200,200,200),rgb(200,200,200))', borderRadius: '2px', boxShadow: '0px 2px 4px rgba(0,0,0,0.1)' }}></div>
        </div>
    );

    // ── Form body ─────────────────────────────────────────────────────────
    const formBody = (
        <>
            {showOperatorAuth && (
                <OperatorAuthDialog
                    visible={showOperatorAuth}
                    key={operatorAuthKey}
                    onHide={() => setShowOperatorAuth(false)}
                    onVerified={onOperatorVerified}
                    orgSettings={JSON.parse(localStorage.getItem('organizationSettings') || '{}')}
                    onSuccess={onOperatorVerified}
                    onCancel={onOperatorCancel}
                />
            )}
            {!showOperatorAuth && (
                <form id="invoiceForm" ref={invoiceFormRef} onSubmit={(e) => handleSubmit(e, isCompletingRef.current)}>
                    <div style={{ borderRadius: '12px' }}>
                        <Accordion multiple activeIndex={[0]} className="mb-4">
                            <AccordionTab
                                header={
                                    <div className="flex flex-wrap align-items-center gap-4 sm:gap-4">
                                        <div>Main details:</div>
                                        <div>
                                            {invoice?.id ? (
                                                invoice?.operator_name && (
                                                    <div className="text-sm text-green-700 mt-1">
                                                        <i className="pi pi-user mr-1" />
                                                        Operator: <strong>{invoice.operator_name}</strong>
                                                    </div>
                                                )
                                            ) : (
                                                invoiceOperator?.operator_name && (
                                                    <div className="text-sm text-green-700 mt-1">
                                                        <i className="pi pi-user mr-1" />
                                                        Operator: <strong>{invoiceOperator.operator_name}</strong>
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    </div>
                                }
                            >
                                <div style={{ overflow: "hidden", padding: "0 0.5rem" }}>
                                <div className="formgrid grid p-fluid">
                                    <div className="field lg:col-2">
                                        <label htmlFor="customer" className="font-bold">Customer:</label>
                                        <div className="p-inputgroup">
                                            <AutoComplete
                                                value={invoice.customer} ref={cusinputRef}
                                                suggestions={filteredCustomers}
                                                completeMethod={(e) => handleSearchCustomer(e.query)}
                                                field="displayLabel"
                                                onChange={(e) => {
                                                    const c = e.value;
                                                    setInvoice(prev => ({
                                                        ...prev,
                                                        customer: c,
                                                        invoiced_customer: `${c?.title ?? ''} ${c?.displayLabel ?? ''}`.trim()
                                                    }));
                                                    if (c?.id) handleSearchPrescriptions(c.id);
                                                    applyCustomerLoyaltyDiscount(c);
                                                }}
                                                id="customer" disabled={isReadOnly} placeholder="Select Customer"
                                            />
                                            <span
                                                className={`p-inputgroup-addon ${isReadOnly || !canManageCustomers ? 'disabled' : ''}`}
                                                onClick={() => !isReadOnly && canManageCustomers && setCustomerDialogVisible(true)}
                                                style={{ cursor: isReadOnly || !canManageCustomers ? 'not-allowed' : 'pointer',
                                                        opacity: isReadOnly || !canManageCustomers ? 0.5 : 1 }}
                                            ><b>+</b></span>
                                        </div>
                                    </div>
                                    <div className="field lg:col-2">
                                        <label htmlFor="customer_name" className="font-bold">Invoiced Customer:</label>
                                        <InputText
                                            value={invoice?.invoiced_customer || ''}
                                            onChange={(e) => setInvoice((prev) => ({ ...prev, invoiced_customer: e.target.value }))}
                                            id="customer_name" disabled={isReadOnly} placeholder="Invoiced Customer"
                                        />
                                    </div>
                                    <div className="field lg:col-2">
                                        <label htmlFor="prescription" className="font-bold">Prescription:</label>
                                        <div className="p-inputgroup">
                                            <Dropdown
                                                id="prescription" value={invoice.prescription}
                                                options={filteredPrescriptions}
                                                onChange={handlePrescriptionChange}
                                                optionLabel="label" placeholder="Select a Prescription"
                                                disabled={!invoice.customer?.id || isReadOnly} showClear
                                            />
                                            <span
                                                className={`p-inputgroup-addon ${isReadOnly || !canManagePrescriptions ? 'disabled' : ''}`}
                                                onClick={() => {
                                                    if (!isReadOnly && invoice.customer?.id && canManagePrescriptions) {
                                                        setPrescription((prev) => ({ ...prev, customer: invoice.customer }));
                                                        setPrescriptionDialogVisible(true);
                                                    }
                                                }}
                                                style={{ cursor: isReadOnly || !invoice.customer?.id || !canManagePrescriptions ? 'not-allowed' : 'pointer', opacity: isReadOnly || !invoice.customer?.id || !canManagePrescriptions ? 0.5 : 1, border: isReadOnly || !invoice.customer?.id ? 'None' : '' }}
                                            ><b>+</b></span>
                                        </div>
                                    </div>
                                    <div className="field lg:col-2">
                                        <label htmlFor="duration" className="font-bold">Duration(Days):</label>
                                        <InputNumber id="duration" value={invoice.duration}
                                            onValueChange={(e) => handleDurationChange(e.value)}
                                            placeholder="Set duration in days" disabled={isReadOnly} />
                                    </div>
                                    <div className="field lg:col-2">
                                        <label htmlFor="date" className="font-bold">Invoice Date:</label>
                                        <Calendar id="date"
                                            value={isReadOnly ? new Date(invoice.date) : currentTime}
                                            showTime hourFormat="12" disabled />
                                    </div>
                                    <div className="field lg:col-2">
                                        <label htmlFor="status" className="font-bold">Status:</label>
                                        <InputText id="status" value={invoice.status} placeholder="Open" readOnly />
                                    </div>
                                </div>
                                </div>
                            </AccordionTab>
                        </Accordion>
                    </div>

                    {/* Items table */}
                    <div>
                        <DataTable
                            value={invoice.items}
                            className="invoice-table"
                            scrollable
                            scrollHeight="400px"
                            header={itemsTableHeader}
                            selection={selectedItem}
                            onSelectionChange={(e) => {
                                if (e.value && e.value.product && e.value.product.id) setSelectedItem(e.value);
                                else setSelectedItem(null);
                            }}
                        >
                            <Column selectionMode="single" headerStyle={{ width: '3rem' }} />
                            <Column header="#"        body={bodyIndex}    style={{ width: '40px', textAlign: 'center' }} />
                            <Column header="Product"  body={bodyProduct} />
                            <Column header="Quantity" body={bodyQuantity} />
                            <Column header="Duration" body={bodyDuration} />
                            <Column header="Price"    body={bodyPrice} />
                            <Column header="Total"    body={bodyTotal} />
                            <Column header="Batch#"   body={bodyBatch} />
                            <Column header="Expiry"   body={bodyExpiry} />
                            {!isReadOnly && (
                                <Column header="Actions" body={bodyAction} />
                            )}
                        </DataTable>

                        {/* Pricing section */}
                        <Accordion multiple activeIndex={[0]} className="mb-4">
                            <AccordionTab header="Pricing details">
                                <div style={{ overflow: "hidden", padding: "0 0.5rem" }}>
                                <div className="formgrid grid p-fluid">
                                    <div className="field lg:col-2">
                                        <label htmlFor="discount" className="font-bold">Discount:</label>
                                        <div className="p-inputgroup" style={{ position: 'relative' }}>
                                            <InputNumber id="discount" value={invoice.discount}
                                                onValueChange={(e) => {
                                                    const newDiscount = e.value || 0;
                                                    setInvoice(prev => ({ ...prev, discount: newDiscount, isdiscount_per: prev.isdiscount_per }));
                                                    calculatePrices(undefined, newDiscount, undefined);
                                                }}
                                                disabled={isReadOnly || (discountApproval && !discountEditable)}
                                                min={0} placeholder={`Enter ${invoice.isdiscount_per ? 'Percentage' : 'Value'} Discount`} />
                                            <span
                                                className={`p-inputgroup-addon ${isReadOnly || (discountApproval && !discountEditable) ? 'disabled' : ''}`}
                                                style={{ cursor: isReadOnly || (discountApproval && !discountEditable) ? 'not-allowed' : 'pointer', opacity: isReadOnly || (discountApproval && !discountEditable) ? 0.5 : 1 }}
                                                onClick={() => {
                                                    if (!isReadOnly && !(discountApproval && !discountEditable)) {
                                                        setInvoice(prev => ({ ...prev, isdiscount_per: !prev.isdiscount_per, discount: null }));
                                                        calculatePrices(undefined, 0, undefined);
                                                    }
                                                }}
                                            >{invoice.isdiscount_per ? '%' : '$'}</span>
                                            {discountApproval && !discountEditable && (
                                                <FaLock onClick={() => { if (!isReadOnly) { setPasswordPurpose('override'); setPasswordDialogHeader('Enter Price Override Password'); setPasswordDialogVisible(true); } }}
                                                    style={{ position: 'absolute', right: '50px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: '#888', zIndex: 2 }}
                                                    size={15} title="Enter Override Password" />
                                            )}
                                            {discountApproval && discountEditable && (
                                                <FaUnlock style={{ position: 'absolute', right: '50px', top: '50%', transform: 'translateY(-50%)', color: '#4CAF50', zIndex: 2 }}
                                                    size={15} title="Discount Override Enabled" />
                                            )}
                                        </div>
                                    </div>
                                    <div className="field lg:col-2">
                                        <label htmlFor="total_price" className="font-bold">Total Price:</label>
                                        <InputNumber id="total_price" value={invoice.total_price} mode="currency" currency="LKR" readOnly />
                                    </div>
                                    <div className="field lg:col-2">
                                        <label htmlFor="tax_amount" className="font-bold">Tax Amount:</label>
                                        <InputNumber id="tax_amount" value={invoice.tax_amount} mode="currency" currency="LKR" readOnly />
                                    </div>
                                    <div className="field lg:col-2">
                                        <label htmlFor="net_price" className="font-bold">Net Price:</label>
                                        <InputNumber id="net_price" value={invoice.net_price} mode="currency" currency="LKR" readOnly />
                                    </div>
                                    <div className="field lg:col-2">
                                        <label htmlFor="paid" className="font-bold">Paid Amount:</label>
                                        <InputNumber id="paid" value={invoice.paid_amount} mode="currency" currency="LKR" readOnly />
                                    </div>
                                    <div className="field lg:col-2">
                                        <label htmlFor="balance" className="font-bold">Balance Amount:</label>
                                        <InputNumber id="balance" value={(invoice.net_price || 0) - (invoice.paid_amount || 0)} mode="currency" currency="LKR" readOnly />
                                    </div>
                                </div>
                                </div>
                            </AccordionTab>
                        </Accordion>
                    </div>
                </form>
            )}
        </>
    );

    // ── Image panel (rendered inside the dialog, no z-index issues) ──────
    const imagePanelContent = portalUploadData?.file_url && showImagePanel && (
        <div style={{
            width: "30%", minWidth: 300, flexShrink: 0,
            background: "#1a1a2e", display: "flex", flexDirection: "column",
            borderRight: "1px solid #333",
        }}>
            <div style={{
                padding: "10px 14px", background: "#0f0f1a",
                borderBottom: "1px solid #333",
                display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
                <div style={{ color: "white", fontSize: 13, fontWeight: 600, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {portalUploadData.original_name}
                </div>
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                    <a href={portalUploadData.file_url} target="_blank" rel="noreferrer"
                        style={{ color: "#60a5fa", fontSize: 11, textDecoration: "none", display: "flex", alignItems: "center", gap: 4, padding: "4px 8px", borderRadius: 6, border: "1px solid #334" }}>
                        <i className="pi pi-external-link" style={{ fontSize: 11 }} /> Full
                    </a>
                    <button onClick={() => setShowImagePanel(false)}
                        style={{ background: "none", border: "1px solid #334", borderRadius: 6, color: "#888", cursor: "pointer", padding: "4px 8px", fontSize: 11, display: "flex", alignItems: "center", gap: 4 }}>
                        <i className="pi pi-times" style={{ fontSize: 10 }} /> Hide
                    </button>
                </div>
            </div>
            <div style={{ flex: 1, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", padding: 12 }}>
                {portalUploadData.file_type === "pdf" ? (
                    <iframe src={portalUploadData.file_url} title="Prescription" style={{ width: "100%", height: "100%", border: "none" }} />
                ) : (
                    <img src={portalUploadData.file_url} alt="Uploaded prescription"
                        style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                )}
            </div>
        </div>
    );

    // ── fromUpload: inline panel, no Dialog — same as CreatePrescriptionDialog ──
    if (fromUpload) {
        if (!visible) return null;
        return (
            <div style={{ position: 'absolute', inset: 0, zIndex: 10, background: '#fff', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <div style={{ borderBottom: '1px solid #e5e7eb', flexShrink: 0, padding: '4px 0' }}>
                    {headerButtons}
                </div>
                <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
                    {imagePanelContent}
                    <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
                        {formBody}
                    </div>
                </div>
            </div>
        );
    }

    // ── Normal: full-screen Dialog ────────────────────────────────────────
    return (
        <Dialog
            header={headerButtons}
            visible={visible}
            onHide={onHide}
            modal draggable={false} closable={false} maximized
            breakpoints={{ '960px': '75vw', '641px': '90vw' }}
        >
            <div style={{ display: 'flex', height: '100%', overflow: 'hidden', margin: '-1rem' }}>
                {imagePanelContent}
                <div style={{ flex: 1, overflow: 'auto', padding: '1rem' }}>
                    {formBody}
                </div>
            </div>
        </Dialog>
    );
};

export default CreateInvoiceDialog;